import React, { useEffect, useRef, useState } from 'react';
import { Browser } from '@capacitor/browser';
import { Button, CircularProgress, Flex, Image, Text } from '@chakra-ui/react';
import { Auth } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { signOutOfAuthSession } from 'clipsal-cortex-utils/src/auth/cached-session';
import { IS_NATIVE } from 'clipsal-cortex-utils/src/constants/common-constants';

import loggedOutImg from '../../../assets/images/logged_out.svg';
import { COMPONENT_MIN_HEIGHT, IS_RUNNING_TESTS } from '../../../common/constants';
import { clearGoogleAnalyticsIdentifiers } from '../../../utils/common/tracking-privacy';
import {
  resetOnboardingFunnelTimer,
  trackDynatraceLogoutResult,
  trackDynatraceLogoutStageResult,
  trackDynatraceLogoutStarted,
} from '../../../utils/native/dynatrace-events';
import { resetConsentManagement } from '../../account/settings/consent-management';
import { resetEnrollAttemptCounter } from '../../ev-charger/device-setup/deviceSetupSlice';
import { selectUser } from '../../user/userSlice';
import { getCognitoLogOutUrl, getIdpLogOutUrl } from '../auth-helpers';
import styles from './Logout.module.scss';

type NativeLogoutFailureReason = 'browser_logout_timeout' | 'browser_open_failed' | 'browser_close_failed';
type NativeLogoutBrowserResult = { succeeded: boolean; failureReason?: NativeLogoutFailureReason };

export function Logout() {
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const [isLoaded, setLoaded] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const userIdAtLogoutStart = useRef(user.userID);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    /**
     * Log out of the application.
     * This is done in 3 steps:
     *  1. Log out of the EIM IdP session
     *  2. Log out of the Cognito session
     *  3. Clear out everything else (reset Redux store and call `await Auth.signOut()`)
     */
    async function logOut() {
      if (IS_RUNNING_TESTS) {
        setLoaded(true);
        return;
      }

      trackDynatraceLogoutStarted();

      // Step 1: Resolve the IDP logout URL while the Amplify session is still active.
      // getIdpLogOutUrl() calls Auth.currentAuthenticatedUser() internally — it must
      // run before signOutOfAuthSession clears the Amplify session.
      let idpLogOutUrl: string | null = null;
      try {
        idpLogOutUrl = await getIdpLogOutUrl();
        trackDynatraceLogoutStageResult('idp_logout_url_resolution', 'success');
      } catch (error) {
        console.error('Error getting IDP log out URL', error);
        trackDynatraceLogoutStageResult('idp_logout_url_resolution', 'failure', 'idp_logout_url_unavailable');
      }

      // Step 2 (web only): store the EIM logout URL in sessionStorage BEFORE calling
      // signOutOfAuthSession. Amplify v4 with OAuth uses urlOpener for signOut, which
      // calls Browser.open(_self) and navigates the page away immediately — any code
      // after signOutOfAuthSession is unreachable on web.
      // sessionStorage survives the redirect since it stays in the same browser tab.
      if (!IS_NATIVE && idpLogOutUrl) {
        sessionStorage.setItem('pendingIdpLogout', idpLogOutUrl);
      }

      // Step 3: Sign out of Amplify and clear the local token cache.
      // Storage.remove runs before Auth.signOut() inside signOutOfAuthSession so the
      // cache is always cleared even if signOut navigates away.
      // On web, Auth.signOut() also navigates to the Cognito logout URL (via urlOpener),
      // clearing the Cognito session cookie and redirecting back to the app.
      // On native, it only clears local tokens — browser sessions are handled in Step 4.
      await resetConsentManagement();
      clearGoogleAnalyticsIdentifiers();
      try {
        await signOutOfAuthSession(Auth);
        trackDynatraceLogoutStageResult('cognito_signout', 'success');
      } catch (error) {
        console.error('Error during sign out:', error);
        trackDynatraceLogoutStageResult('cognito_signout', 'failure', 'cognito_signout_failed');
      }

      // Step 4: Clean up local app state.
      // On web this may not execute (Auth.signOut navigated away), but that's fine —
      // the Redux store resets on page reload and localStorage is cleared on next mount.
      const userId = userIdAtLogoutStart.current;
      localStorage.removeItem(`${userId}_firstNameEIM`);
      localStorage.removeItem(`${userId}_lastNameEIM`);
      resetOnboardingFunnelTimer();
      resetEnrollAttemptCounter();
      dispatch({ type: 'store/reset' });
      if (IS_NATIVE) trackDynatraceLogoutStageResult('local_session_reset', 'success');

      // Step 5 (native only): kill remote browser session via the in-app browser.
      // On web, Auth.signOut() already handled the Cognito logout redirect (Step 3),
      // and Login.tsx will handle the EIM logout via pendingIdpLogout (set in Step 2).
      if (IS_NATIVE) {
        try {
          // Open `url` in the in-app browser, close it on first page load, and resolve
          // only when `browserFinished` fires (i.e. the browser overlay is fully gone).
          const openAndCloseOnLoad = async (url: string): Promise<NativeLogoutBrowserResult> => {
            return new Promise<NativeLogoutBrowserResult>((resolve) => {
              let pageLoadedListener: { remove: () => void } | null = null;
              let finishedListener: { remove: () => void } | null = null;
              let hasResolved = false;
              const timeoutHandle: { value?: ReturnType<typeof setTimeout> } = {};

              const finish = (succeeded: boolean, failureReason?: NativeLogoutFailureReason) => {
                if (hasResolved) return;
                hasResolved = true;
                if (timeoutHandle.value) clearTimeout(timeoutHandle.value);
                pageLoadedListener?.remove();
                finishedListener?.remove();
                resolve({ succeeded, ...(failureReason ? { failureReason } : {}) });
              };

              // Safety timeout: if browser doesn't complete in 10 seconds, force resolve
              timeoutHandle.value = setTimeout(() => {
                console.warn('Browser logout timeout on iOS, resolving anyway');
                finish(false, 'browser_logout_timeout');
              }, 10000);

              Browser.addListener('browserFinished', () => {
                finish(true);
              }).then((listener) => {
                finishedListener = listener;
              });

              Browser.addListener('browserPageLoaded', async () => {
                if (pageLoadedListener) {
                  pageLoadedListener.remove();
                  pageLoadedListener = null;
                  await Browser.close().catch((error) => {
                    console.error('Error closing browser', error);
                    finish(false, 'browser_close_failed');
                  });
                }
              }).then((listener) => {
                pageLoadedListener = listener;
              });

              Browser.open({ url, windowName: '_self' }).catch((error) => {
                console.error('Error opening browser', error);
                finish(false, 'browser_open_failed');
              });
            });
          };

          // Single-hop native remote logout to reduce visible open/close flashing.
          // Prefer IDP logout (main source of silent re-auth regression), and fall back
          // to Cognito logout if IDP URL could not be resolved.
          const browserLogoutResult = await openAndCloseOnLoad(idpLogOutUrl ?? getCognitoLogOutUrl());
          trackDynatraceLogoutStageResult(
            'native_logout_browser',
            browserLogoutResult.succeeded ? 'success' : 'failure',
            browserLogoutResult.failureReason
          );
          trackDynatraceLogoutResult('success');
        } catch (error) {
          console.error('Error during native browser logout', error);
          trackDynatraceLogoutStageResult('native_logout_browser', 'failure', 'browser_open_failed');
          trackDynatraceLogoutResult('success');
        }
      } else {
        // Web: Auth.signOut() (Step 3) already navigated to Cognito logout via urlOpener,
        // clearing the Cognito session and redirecting back to the app. This branch is
        // unreachable in practice, but kept as an explicit fallback.
        window.location.href = getCognitoLogOutUrl();
      }

      setLoaded(true);
    }

    logOut();
    // Intentionally run once on mount. Including user.userID causes the logout flow
    // to re-run after store/reset updates user state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    setTimeout(() => {
      setShouldRedirect(true);
    }, 7000);
  });

  useEffect(() => {
    if (isLoaded && shouldRedirect) navigate('/');
  }, [isLoaded, navigate, shouldRedirect]);

  return isLoaded ? (
    <>
      <div className={styles.container} id="logged-out-card">
        <div className={styles.logoutContainer}>
          <Image mb={4} w="60%" src={loggedOutImg} alt="Page not found" />
          <Text mb={2} fontSize="md">
            {t('Account.you have logged out')}
          </Text>
          <Button variant="outline">
            <Link to="/login">{t('Account.click here to log in')}</Link>
          </Button>
        </div>
      </div>
    </>
  ) : (
    <Flex direction="column" height={COMPONENT_MIN_HEIGHT} align="center" justify="center">
      <Text fontSize="lg" mb={2}>
        {t('Common.loading')}
      </Text>
      <CircularProgress isIndeterminate color="green" />
    </Flex>
  );
}
